import { normalizePrintSettings } from '../lib/printing';
import { isPrintStationEnabled } from '../lib/printStation';

const LOCAL_PRINTER_SERVER_URL = 'http://localhost:3001';

function getTargetAddressSpace(url) {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
      return 'loopback';
    }
  } catch (_error) {}
  return 'local';
}

function resolvePrintingConfig(printingConfig) {
  const printing = normalizePrintSettings(printingConfig);
  if (!isPrintStationEnabled()) return printing;
  return {
    ...printing,
    printingEnabled: true,
    printingMode: 'server',
    printerServerUrl: LOCAL_PRINTER_SERVER_URL,
    fallbackToBrowserPrint: false,
  };
}

const createManagedError = (message) => {
  const error = new Error(message);
  error.isPrinterError = true;
  return error;
};

async function postToPrinterServer(path, type, printing, payload) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${printing.printerServerUrl}${path}`, {
      method: 'POST',
      // Chrome solicita permiso explícito cuando un sitio HTTPS accede a un
      // servicio HTTP dentro de la red local (impresora del establecimiento).
      targetAddressSpace: getTargetAddressSpace(printing.printerServerUrl),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        payload,
      }),
      signal: controller.signal,
    });

    const body = await response.json().catch(() => null);
    if (!response.ok || !body?.ok) {
      throw createManagedError(body?.error || `Printer server respondio con ${response.status}`);
    }

    return body;
  } catch (error) {
    if (error?.isPrinterError) throw error;
    if (error?.name === 'AbortError') {
      throw createManagedError('Tiempo de espera agotado al intentar imprimir.');
    }
    throw createManagedError(error?.message || 'No se pudo conectar con el servidor de impresion.');
  } finally {
    clearTimeout(timeoutId);
  }
}

async function runBrowserPrint(browserPrint) {
  if (typeof browserPrint !== 'function') {
    throw createManagedError('No hay un metodo de impresion del navegador configurado.');
  }
  await browserPrint();
  return { ok: true, mode: 'browser' };
}

async function runPrintJob(path, payload, printingConfig, options = {}) {
  const printing = resolvePrintingConfig(printingConfig);
  const { browserPrint } = options;

  if (!printing.printingEnabled) {
    return { ok: false, skipped: true, reason: 'disabled' };
  }

  if (printing.printingMode === 'browser') {
    return runBrowserPrint(browserPrint);
  }

  if (!printing.printerServerUrl) {
    const error = createManagedError('Configuracion incompleta: falta la URL del printer-server.');
    if (printing.fallbackToBrowserPrint && typeof browserPrint === 'function') {
      await browserPrint();
      return { ok: true, mode: 'browser-fallback', warning: error.message };
    }
    throw error;
  }

  try {
    const type = path.includes('kitchen') ? 'kitchen' : path.includes('test') ? 'test' : 'ticket';
    const response = await postToPrinterServer(path, type, printing, payload);
    return { ...response, mode: 'server' };
  } catch (error) {
    if (printing.fallbackToBrowserPrint && typeof browserPrint === 'function') {
      await browserPrint();
      return { ok: true, mode: 'browser-fallback', warning: error.message };
    }
    throw error;
  }
}

export function printTicket(sale, printingConfig, options = {}) {
  return runPrintJob('/print/ticket', sale, printingConfig, options);
}

export function printKitchen(order, printingConfig, options = {}) {
  return runPrintJob('/print/kitchen', order, printingConfig, options);
}

export function printTest(printingConfig, payload = {}) {
  const printing = resolvePrintingConfig(printingConfig);
  if (!printing.printingEnabled) {
    throw createManagedError('La impresion esta desactivada en configuracion.');
  }
  if (!printing.printerServerUrl) {
    throw createManagedError('Configura la URL del printer-server antes de probar.');
  }
  return postToPrinterServer('/print/test', 'test', printing, payload);
}

async function probePrinterServer(baseUrl, timeoutMs = 900) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${baseUrl}/health`, {
      method: 'GET',
      targetAddressSpace: getTargetAddressSpace(baseUrl),
      cache: 'no-store',
      signal: controller.signal,
    });
    const body = await response.json().catch(() => null);
    return response.ok && body?.ok === true
      ? { url: baseUrl, health: body }
      : null;
  } catch (_error) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function discoverPrinterServer(configuredUrl, onProgress = () => {}) {
  const rawUrl = String(configuredUrl || '').trim().replace(/\/$/, '');
  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch (_error) {
    throw createManagedError('Escribe primero una URL local de referencia, por ejemplo http://192.168.0.20:3001.');
  }

  const octets = parsed.hostname.split('.').map(Number);
  const isPrivate = octets.length === 4 && octets.every((value) => Number.isInteger(value) && value >= 0 && value <= 255) && (
    octets[0] === 10 ||
    (octets[0] === 192 && octets[1] === 168) ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31)
  );
  if (!isPrivate) {
    throw createManagedError('La búsqueda automática requiere una dirección IPv4 privada de la red local.');
  }

  onProgress('Comprobando la dirección guardada...');
  const current = await probePrinterServer(rawUrl, 1600);
  if (current) return current;

  const prefix = `${octets[0]}.${octets[1]}.${octets[2]}`;
  const port = parsed.port || '3001';
  const currentHost = octets[3];
  const hosts = Array.from({ length: 254 }, (_, index) => index + 1)
    .filter((host) => host !== currentHost);
  const batchSize = 24;

  for (let offset = 0; offset < hosts.length; offset += batchSize) {
    const batch = hosts.slice(offset, offset + batchSize);
    onProgress(`Buscando printer-server en la red... ${Math.min(offset + batch.length, hosts.length)}/${hosts.length}`);
    const results = await Promise.all(
      batch.map((host) => probePrinterServer(`http://${prefix}.${host}:${port}`, 750))
    );
    const found = results.find(Boolean);
    if (found) return found;
  }

  throw createManagedError(`No se encontró un printer-server activo en ${prefix}.x:${port}.`);
}
